import { useCallback } from "react";
import { ParseOrderRulesToConditionsResult } from "@/features/line-sheet-sets/hooks/use-parse-order-rules-to-conditions";

// 오더룰 파싱 결과를 localStorage에 저장해 새로고침/재접속 후에도 재사용한다.
// (파싱은 LLM 기반이라 수십 초 걸려서, 매번 다시 부르지 않도록 캐싱)
// 저장 단위: LSS + 오더룰 파일. 파일별로 최근 결과 1개씩만 보관(같은 파일 재파싱 시 덮어씀).
// 파일 없이 텍스트로 파싱한 경우는 "text" 버킷에 보관.
export interface StoredOrderRuleParseResult {
  result: ParseOrderRulesToConditionsResult;
  // 어떤 오더룰 파일로 파싱했는지(없으면 텍스트 기반) — 안내용.
  unoFileId: number | null;
  // ISO 8601 — 파싱 시각.
  parsedAt: string;
}

const KEY_PREFIX = "orderRuleParseResult:";

function storageKey(lineSheetSetId: number, unoFileId: number | null) {
  return `${KEY_PREFIX}${lineSheetSetId}:${unoFileId ?? "text"}`;
}

function read(
  lineSheetSetId: number,
  unoFileId: number | null
): StoredOrderRuleParseResult | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(
      storageKey(lineSheetSetId, unoFileId)
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredOrderRuleParseResult;
    if (!parsed || !parsed.result || !Array.isArray(parsed.result.brands)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export default function useStoredOrderRuleParseResult(lineSheetSetId: number) {
  // 특정 파일(또는 텍스트)에 저장된 최근 파싱 결과를 즉시 읽는다.
  const get = useCallback(
    (unoFileId: number | null): StoredOrderRuleParseResult | null =>
      read(lineSheetSetId, unoFileId),
    [lineSheetSetId]
  );

  const save = useCallback(
    (result: ParseOrderRulesToConditionsResult, unoFileId: number | null) => {
      const value: StoredOrderRuleParseResult = {
        result,
        unoFileId: unoFileId ?? null,
        parsedAt: new Date().toISOString(),
      };
      try {
        window.localStorage.setItem(
          storageKey(lineSheetSetId, unoFileId),
          JSON.stringify(value)
        );
      } catch {
        // 용량 초과 등 저장 실패는 무시 — 캐싱은 부가 기능.
      }
    },
    [lineSheetSetId]
  );

  const clear = useCallback(
    (unoFileId: number | null) => {
      try {
        window.localStorage.removeItem(storageKey(lineSheetSetId, unoFileId));
      } catch {
        // ignore
      }
    },
    [lineSheetSetId]
  );

  return { get, save, clear };
}
